/* eslint-disable */
import Long from "long";
import * as _m0 from "protobufjs/minimal";
import { GeoCoordinate } from "../MapData/GeoCoordinate";
import {
  Device,
  deviceToNumber,
  deviceFromJSON,
  deviceToJSON,
} from "../Device";
import { Offer } from "../Offer/Offer";
import { Distances } from "./Distances";
import { UnitInformation } from "./UnitInformation";
import { DebugInformation } from "./DebugInformation";
import { UserRating, TrustYouRating } from "../User/Rating";
import {
  Attribute,
  attributeToNumber,
  attributeFromJSON,
  attributeToJSON,
} from "../Accommodation/Attributes";
import { Image } from "../Media/Image";
import { FeaturedAlternative } from "./FeaturedAlternative";
import { PriceStep } from "../Voucher";

export enum CancellationType {
  None = "None",
  Cancellation = "Cancellation",
  CancellationCost = "CancellationCost",
}

export function cancellationTypeFromJSON(object: any): CancellationType {
  switch (object) {
    case 0:
    case "None":
      return CancellationType.None;
    case 1:
    case "Cancellation":
      return CancellationType.Cancellation;
    case 2:
    case "CancellationCost":
      return CancellationType.CancellationCost;
    default:
      throw new globalThis.Error(
        "Unrecognized enum value " + object + " for enum CancellationType"
      );
  }
}

export function cancellationTypeToJSON(object: CancellationType): string {
  switch (object) {
    case CancellationType.None:
      return "None";
    case CancellationType.Cancellation:
      return "Cancellation";
    case CancellationType.CancellationCost:
      return "CancellationCost";
    default:
      return "UNKNOWN";
  }
}

export function cancellationTypeToNumber(object: CancellationType): number {
  switch (object) {
    case CancellationType.None:
      return 0;
    case CancellationType.Cancellation:
      return 1;
    case CancellationType.CancellationCost:
      return 2;
    default:
      return 0;
  }
}

export interface GoogleTagManagerEvents {
  /** offer click event data */
  click: string;
  /** map click event data */
  clickMap: string;
  /** media layer click event data */
  clickMediaLayer: string;
}

export interface MasterData {
  /** for nudging on S3 */
  hasDirectBeachAccess: boolean;
  /** the regionId the accommodation belongs to */
  regionId: number;
  /** the hotel category */
  category: number;
  /** the type of the hotel category */
  categoryType: MasterData_CategoryType;
  /** the display name of the accommodation */
  name: string;
  /** the street of the accommodations address */
  street: string;
  /** the street number of the accommodations address */
  streetNumber: string;
  /** the postal code of the accommodations address */
  postalCode: string;
  /** the city of the accommodations address */
  city: string;
  /** the region the accommodation belongs to */
  region: string;
  /** the country the accommodation belongs to */
  country: string;
  /** the image of the map position (for S4 header only) */
  mapImage: string;
  /** the accommodation coordinates */
  geoCoordinate?: GeoCoordinate;
}

export enum MasterData_CategoryType {
  Star = "Star",
  OfficialStar = "OfficialStar",
  Circle = "Circle",
}

export function masterData_CategoryTypeFromJSON(
  object: any
): MasterData_CategoryType {
  switch (object) {
    case 0:
    case "Star":
      return MasterData_CategoryType.Star;
    case 1:
    case "OfficialStar":
      return MasterData_CategoryType.OfficialStar;
    case 2:
    case "Circle":
      return MasterData_CategoryType.Circle;
    default:
      throw new globalThis.Error(
        "Unrecognized enum value " +
          object +
          " for enum MasterData_CategoryType"
      );
  }
}

export function masterData_CategoryTypeToJSON(
  object: MasterData_CategoryType
): string {
  switch (object) {
    case MasterData_CategoryType.Star:
      return "Star";
    case MasterData_CategoryType.OfficialStar:
      return "OfficialStar";
    case MasterData_CategoryType.Circle:
      return "Circle";
    default:
      return "UNKNOWN";
  }
}

export function masterData_CategoryTypeToNumber(
  object: MasterData_CategoryType
): number {
  switch (object) {
    case MasterData_CategoryType.Star:
      return 0;
    case MasterData_CategoryType.OfficialStar:
      return 1;
    case MasterData_CategoryType.Circle:
      return 2;
    default:
      return 0;
  }
}

export interface Scarcity {
  isPriceHammer: boolean;
  isTopCustomerRating: boolean;
  isBookedOut: boolean;
  isBestSeller: boolean;
  isHighlyDemanded: boolean;
  isFlexOptionAvailable: boolean;
  isTopLocation: boolean;
  isCustomerRecommendation: boolean;
  isRecentlyBooked: boolean;
  recentlyBookedHours: number;
  interestCount: number;
  recentBookingRegion: string;
  recentlyBookedHotel: string;
}

export interface Rating {
  average: number;
  count: number;
  recommendation: number;
  llmAggregatedSummary: string;
  ratingList: UserRating[];
  trustYouRatingList: TrustYouRating[];
}

export interface Flags {
  /** whether the hotel is a featured hotel determined by specific hotel search */
  isFeaturedHotel: boolean;
  /** whether this accommodation is a holiday home or not (hotel otherwise) */
  isHolidayHome: boolean;
  /** whether this accommodation is adult only or not */
  isAdultOnly: boolean;
  /** @deprecated */
  isBlackFridayDeal: boolean;
  /** whether the offer has a strike price or not */
  hasStrikePrice: boolean;
  /** whether the offer is exclusive or not */
  hasExclusiveBanner: boolean;
  /**
   * should Link be opened in same tab
   *
   * @deprecated
   */
  offerLinkInSameTab: boolean;
  exclusiveBannerDescription: string;
  /** whether the cancellation type */
  cancellationType: CancellationType;
}

export interface LastVisited {
  timestamp: number;
  source: Device;
}

export interface Hotel {
  /** the hotelId */
  id: number;
  recentBookingCount: number;
  description: string;
  hibUrl: string;
  masterData?: MasterData;
  offer?: Offer;
  flags?: Flags;
  media?: Hotel_Media;
  rating?: Rating;
  distances?: Distances;
  /** the holiday home unit summary */
  unitInformation?: UnitInformation;
  scarcity?: Scarcity;
  /** accommodation specific tracking data */
  tagManagerEvents?: GoogleTagManagerEvents;
  debugInformation?: DebugInformation;
  /**
   * use badgeItemList instead
   *
   * @deprecated
   */
  badgeList?: Hotel_BadgeList;
  rankedAttributeList: Attribute[];
  badgeItemList: Hotel_Badge[];
}

export interface Hotel_Media {
  /** whether the accommodation has a video or not */
  hasVideo: boolean;
  /**
   * the overall image count
   *
   * @deprecated
   */
  totalImageCount: number;
  /** the official image count */
  imageCount: number;
  /** the guest image count */
  guestImageCount: number;
  /** the list of images */
  images: Image[];
}

export interface Hotel_BadgeList {
  /**
   * use badgeItemList instead
   *
   * @deprecated
   */
  irBadgeList: Hotel_Badge[];
}

export interface Hotel_Badge {
  title: string;
  description: string;
  params: { [key: string]: string };
  type: Hotel_Badge_Type;
}

export enum Hotel_Badge_Type {
  Unknown = "Unknown",
  TopHotelAward = "TopHotelAward",
  IndividualTravel = "IndividualTravel",
}

export function hotel_Badge_TypeFromJSON(object: any): Hotel_Badge_Type {
  switch (object) {
    case 0:
    case "Unknown":
      return Hotel_Badge_Type.Unknown;
    case 1:
    case "TopHotelAward":
      return Hotel_Badge_Type.TopHotelAward;
    case 2:
    case "IndividualTravel":
      return Hotel_Badge_Type.IndividualTravel;
    default:
      throw new globalThis.Error(
        "Unrecognized enum value " + object + " for enum Hotel_Badge_Type"
      );
  }
}

export function hotel_Badge_TypeToJSON(object: Hotel_Badge_Type): string {
  switch (object) {
    case Hotel_Badge_Type.Unknown:
      return "Unknown";
    case Hotel_Badge_Type.TopHotelAward:
      return "TopHotelAward";
    case Hotel_Badge_Type.IndividualTravel:
      return "IndividualTravel";
    default:
      return "UNKNOWN";
  }
}

export function hotel_Badge_TypeToNumber(object: Hotel_Badge_Type): number {
  switch (object) {
    case Hotel_Badge_Type.Unknown:
      return 0;
    case Hotel_Badge_Type.TopHotelAward:
      return 1;
    case Hotel_Badge_Type.IndividualTravel:
      return 2;
    default:
      return 0;
  }
}

export interface Hotel_Badge_ParamsEntry {
  key: string;
  value: string;
}

export interface Tracking {
  /** the impression tracking entries */
  impressions: string[];
}

export interface MismatchFilter {
  name: string;
  value: string;
}

export interface MetaData {
  /** whether the search contains children or not (required until search form will be implemented in React) */
  withChildren: boolean;
  /** the complete traveller count (required until search form will be implemented in React) */
  travellerCount: number;
  /** the redirect url if redirect condition occurs */
  redirectUrl: string;
  /** the searches destination name */
  autoCompleterDestinationName: string;
  /** default offer link. used to build complete url links on every hotel id in frontend */
  defaultOfferLink: string;
  /** the list tracking data */
  tracking?: Tracking;
  /** the additional voucher data for the current search */
  voucher?: MetaData_Voucher;
  scarcity?: MetaData_Scarcity;
  historyData?: MetaData_HistoryData;
  featuredMismatchFilterList: MismatchFilter[];
  featuredAlternativeList: FeaturedAlternative[];
}

export interface MetaData_Voucher {
  minPrice: number;
  additionalAppRedeemAmount: number;
  conditionsUrl: string;
  priceSteps: PriceStep[];
}

export interface MetaData_Scarcity {
  highlyDemanded: boolean;
  recentBookingRegion: string;
}

export interface MetaData_HistoryData {
  imageUrl: string;
  addItem?: MetaData_HistoryData_Item;
}

export interface MetaData_HistoryData_Item {
  id: string;
  url: string;
  price: number;
  regionName: string;
}

export interface HotelList {
  /** the total result count */
  count: number;
  /** the minimum price of all results */
  priceMin: number;
  /** the maximum price of all results */
  priceMax: number;
  /** the url to poll against */
  pollUrl: string;
  /** the hotelIds in display order */
  itemOrder: number[];
  /** the items, indexed by their id */
  items: { [key: string]: Hotel };
  /** the list meta data */
  meta?: MetaData;
}

export interface HotelList_ItemsEntry {
  key: string;
  value?: Hotel;
}

function createBaseGoogleTagManagerEvents(): GoogleTagManagerEvents {
  return { click: "", clickMap: "", clickMediaLayer: "" };
}

export const GoogleTagManagerEvents = {
  encode(
    message: GoogleTagManagerEvents,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.click !== "") {
      writer.uint32(10).string(message.click);
    }
    if (message.clickMap !== "") {
      writer.uint32(18).string(message.clickMap);
    }
    if (message.clickMediaLayer !== "") {
      writer.uint32(26).string(message.clickMediaLayer);
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): GoogleTagManagerEvents {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGoogleTagManagerEvents();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.click = reader.string();
          break;
        case 2:
          message.clickMap = reader.string();
          break;
        case 3:
          message.clickMediaLayer = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): GoogleTagManagerEvents {
    return {
      click: isSet(object.click) ? String(object.click) : "",
      clickMap: isSet(object.clickMap) ? String(object.clickMap) : "",
      clickMediaLayer: isSet(object.clickMediaLayer)
        ? String(object.clickMediaLayer)
        : "",
    };
  },

  toJSON(message: GoogleTagManagerEvents): unknown {
    const obj: any = {};
    message.click !== undefined && (obj.click = message.click);
    message.clickMap !== undefined && (obj.clickMap = message.clickMap);
    message.clickMediaLayer !== undefined &&
      (obj.clickMediaLayer = message.clickMediaLayer);
    return obj;
  },

  fromPartial(
    object: DeepPartial<GoogleTagManagerEvents>
  ): GoogleTagManagerEvents {
    const message = createBaseGoogleTagManagerEvents();
    message.click = object.click ?? "";
    message.clickMap = object.clickMap ?? "";
    message.clickMediaLayer = object.clickMediaLayer ?? "";
    return message;
  },
};

function createBaseMasterData(): MasterData {
  return {
    hasDirectBeachAccess: false,
    regionId: 0,
    category: 0,
    categoryType: MasterData_CategoryType.Star,
    name: "",
    street: "",
    streetNumber: "",
    postalCode: "",
    city: "",
    region: "",
    country: "",
    mapImage: "",
    geoCoordinate: undefined,
  };
}

export const MasterData = {
  encode(
    message: MasterData,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.hasDirectBeachAccess === true) {
      writer.uint32(112).bool(message.hasDirectBeachAccess);
    }
    if (message.regionId !== 0) {
      writer.uint32(96).int32(message.regionId);
    }
    if (message.category !== 0) {
      writer.uint32(21).float(message.category);
    }
    if (message.categoryType !== MasterData_CategoryType.Star) {
      writer
        .uint32(88)
        .int32(masterData_CategoryTypeToNumber(message.categoryType));
    }
    if (message.name !== "") {
      writer.uint32(10).string(message.name);
    }
    if (message.street !== "") {
      writer.uint32(42).string(message.street);
    }
    if (message.streetNumber !== "") {
      writer.uint32(50).string(message.streetNumber);
    }
    if (message.postalCode !== "") {
      writer.uint32(58).string(message.postalCode);
    }
    if (message.city !== "") {
      writer.uint32(66).string(message.city);
    }
    if (message.region !== "") {
      writer.uint32(74).string(message.region);
    }
    if (message.country !== "") {
      writer.uint32(82).string(message.country);
    }
    if (message.mapImage !== "") {
      writer.uint32(122).string(message.mapImage);
    }
    if (message.geoCoordinate !== undefined) {
      GeoCoordinate.encode(
        message.geoCoordinate,
        writer.uint32(106).fork()
      ).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MasterData {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMasterData();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 14:
          message.hasDirectBeachAccess = reader.bool();
          break;
        case 12:
          message.regionId = reader.int32();
          break;
        case 2:
          message.category = reader.float();
          break;
        case 11:
          message.categoryType = masterData_CategoryTypeFromJSON(
            reader.int32()
          );
          break;
        case 1:
          message.name = reader.string();
          break;
        case 5:
          message.street = reader.string();
          break;
        case 6:
          message.streetNumber = reader.string();
          break;
        case 7:
          message.postalCode = reader.string();
          break;
        case 8:
          message.city = reader.string();
          break;
        case 9:
          message.region = reader.string();
          break;
        case 10:
          message.country = reader.string();
          break;
        case 15:
          message.mapImage = reader.string();
          break;
        case 13:
          message.geoCoordinate = GeoCoordinate.decode(reader, reader.uint32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MasterData {
    return {
      hasDirectBeachAccess: isSet(object.hasDirectBeachAccess)
        ? Boolean(object.hasDirectBeachAccess)
        : false,
      regionId: isSet(object.regionId) ? Number(object.regionId) : 0,
      category: isSet(object.category) ? Number(object.category) : 0,
      categoryType: isSet(object.categoryType)
        ? masterData_CategoryTypeFromJSON(object.categoryType)
        : MasterData_CategoryType.Star,
      name: isSet(object.name) ? String(object.name) : "",
      street: isSet(object.street) ? String(object.street) : "",
      streetNumber: isSet(object.streetNumber)
        ? String(object.streetNumber)
        : "",
      postalCode: isSet(object.postalCode) ? String(object.postalCode) : "",
      city: isSet(object.city) ? String(object.city) : "",
      region: isSet(object.region) ? String(object.region) : "",
      country: isSet(object.country) ? String(object.country) : "",
      mapImage: isSet(object.mapImage) ? String(object.mapImage) : "",
      geoCoordinate: isSet(object.geoCoordinate)
        ? GeoCoordinate.fromJSON(object.geoCoordinate)
        : undefined,
    };
  },

  toJSON(message: MasterData): unknown {
    const obj: any = {};
    message.hasDirectBeachAccess !== undefined &&
      (obj.hasDirectBeachAccess = message.hasDirectBeachAccess);
    message.regionId !== undefined &&
      (obj.regionId = Math.round(message.regionId));
    message.category !== undefined && (obj.category = message.category);
    message.categoryType !== undefined &&
      (obj.categoryType = masterData_CategoryTypeToJSON(message.categoryType));
    message.name !== undefined && (obj.name = message.name);
    message.street !== undefined && (obj.street = message.street);
    message.streetNumber !== undefined &&
      (obj.streetNumber = message.streetNumber);
    message.postalCode !== undefined && (obj.postalCode = message.postalCode);
    message.city !== undefined && (obj.city = message.city);
    message.region !== undefined && (obj.region = message.region);
    message.country !== undefined && (obj.country = message.country);
    message.mapImage !== undefined && (obj.mapImage = message.mapImage);
    message.geoCoordinate !== undefined &&
      (obj.geoCoordinate = message.geoCoordinate
        ? GeoCoordinate.toJSON(message.geoCoordinate)
        : undefined);
    return obj;
  },

  fromPartial(object: DeepPartial<MasterData>): MasterData {
    const message = createBaseMasterData();
    message.hasDirectBeachAccess = object.hasDirectBeachAccess ?? false;
    message.regionId = object.regionId ?? 0;
    message.category = object.category ?? 0;
    message.categoryType = object.categoryType ?? MasterData_CategoryType.Star;
    message.name = object.name ?? "";
    message.street = object.street ?? "";
    message.streetNumber = object.streetNumber ?? "";
    message.postalCode = object.postalCode ?? "";
    message.city = object.city ?? "";
    message.region = object.region ?? "";
    message.country = object.country ?? "";
    message.mapImage = object.mapImage ?? "";
    message.geoCoordinate =
      object.geoCoordinate !== undefined && object.geoCoordinate !== null
        ? GeoCoordinate.fromPartial(object.geoCoordinate)
        : undefined;
    return message;
  },
};

function createBaseScarcity(): Scarcity {
  return {
    isPriceHammer: false,
    isTopCustomerRating: false,
    isBookedOut: false,
    isBestSeller: false,
    isHighlyDemanded: false,
    isFlexOptionAvailable: false,
    isTopLocation: false,
    isCustomerRecommendation: false,
    isRecentlyBooked: false,
    recentlyBookedHours: 0,
    interestCount: 0,
    recentBookingRegion: "",
    recentlyBookedHotel: "",
  };
}

export const Scarcity = {
  encode(
    message: Scarcity,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.isPriceHammer === true) {
      writer.uint32(8).bool(message.isPriceHammer);
    }
    if (message.isTopCustomerRating === true) {
      writer.uint32(16).bool(message.isTopCustomerRating);
    }
    if (message.isBookedOut === true) {
      writer.uint32(24).bool(message.isBookedOut);
    }
    if (message.isBestSeller === true) {
      writer.uint32(32).bool(message.isBestSeller);
    }
    if (message.isHighlyDemanded === true) {
      writer.uint32(40).bool(message.isHighlyDemanded);
    }
    if (message.isFlexOptionAvailable === true) {
      writer.uint32(48).bool(message.isFlexOptionAvailable);
    }
    if (message.isTopLocation === true) {
      writer.uint32(56).bool(message.isTopLocation);
    }
    if (message.isCustomerRecommendation === true) {
      writer.uint32(64).bool(message.isCustomerRecommendation);
    }
    if (message.isRecentlyBooked === true) {
      writer.uint32(72).bool(message.isRecentlyBooked);
    }
    if (message.recentlyBookedHours !== 0) {
      writer.uint32(80).uint32(message.recentlyBookedHours);
    }
    if (message.interestCount !== 0) {
      writer.uint32(88).uint32(message.interestCount);
    }
    if (message.recentBookingRegion !== "") {
      writer.uint32(98).string(message.recentBookingRegion);
    }
    if (message.recentlyBookedHotel !== "") {
      writer.uint32(106).string(message.recentlyBookedHotel);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Scarcity {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScarcity();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.isPriceHammer = reader.bool();
          break;
        case 2:
          message.isTopCustomerRating = reader.bool();
          break;
        case 3:
          message.isBookedOut = reader.bool();
          break;
        case 4:
          message.isBestSeller = reader.bool();
          break;
        case 5:
          message.isHighlyDemanded = reader.bool();
          break;
        case 6:
          message.isFlexOptionAvailable = reader.bool();
          break;
        case 7:
          message.isTopLocation = reader.bool();
          break;
        case 8:
          message.isCustomerRecommendation = reader.bool();
          break;
        case 9:
          message.isRecentlyBooked = reader.bool();
          break;
        case 10:
          message.recentlyBookedHours = reader.uint32();
          break;
        case 11:
          message.interestCount = reader.uint32();
          break;
        case 12:
          message.recentBookingRegion = reader.string();
          break;
        case 13:
          message.recentlyBookedHotel = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Scarcity {
    return {
      isPriceHammer: isSet(object.isPriceHammer)
        ? Boolean(object.isPriceHammer)
        : false,
      isTopCustomerRating: isSet(object.isTopCustomerRating)
        ? Boolean(object.isTopCustomerRating)
        : false,
      isBookedOut: isSet(object.isBookedOut)
        ? Boolean(object.isBookedOut)
        : false,
      isBestSeller: isSet(object.isBestSeller)
        ? Boolean(object.isBestSeller)
        : false,
      isHighlyDemanded: isSet(object.isHighlyDemanded)
        ? Boolean(object.isHighlyDemanded)
        : false,
      isFlexOptionAvailable: isSet(object.isFlexOptionAvailable)
        ? Boolean(object.isFlexOptionAvailable)
        : false,
      isTopLocation: isSet(object.isTopLocation)
        ? Boolean(object.isTopLocation)
        : false,
      isCustomerRecommendation: isSet(object.isCustomerRecommendation)
        ? Boolean(object.isCustomerRecommendation)
        : false,
      isRecentlyBooked: isSet(object.isRecentlyBooked)
        ? Boolean(object.isRecentlyBooked)
        : false,
      recentlyBookedHours: isSet(object.recentlyBookedHours)
        ? Number(object.recentlyBookedHours)
        : 0,
      interestCount: isSet(object.interestCount)
        ? Number(object.interestCount)
        : 0,
      recentBookingRegion: isSet(object.recentBookingRegion)
        ? String(object.recentBookingRegion)
        : "",
      recentlyBookedHotel: isSet(object.recentlyBookedHotel)
        ? String(object.recentlyBookedHotel)
        : "",
    };
  },

  toJSON(message: Scarcity): unknown {
    const obj: any = {};
    message.isPriceHammer !== undefined &&
      (obj.isPriceHammer = message.isPriceHammer);
    message.isTopCustomerRating !== undefined &&
      (obj.isTopCustomerRating = message.isTopCustomerRating);
    message.isBookedOut !== undefined &&
      (obj.isBookedOut = message.isBookedOut);
    message.isBestSeller !== undefined &&
      (obj.isBestSeller = message.isBestSeller);
    message.isHighlyDemanded !== undefined &&
      (obj.isHighlyDemanded = message.isHighlyDemanded);
    message.isFlexOptionAvailable !== undefined &&
      (obj.isFlexOptionAvailable = message.isFlexOptionAvailable);
    message.isTopLocation !== undefined &&
      (obj.isTopLocation = message.isTopLocation);
    message.isCustomerRecommendation !== undefined &&
      (obj.isCustomerRecommendation = message.isCustomerRecommendation);
    message.isRecentlyBooked !== undefined &&
      (obj.isRecentlyBooked = message.isRecentlyBooked);
    message.recentlyBookedHours !== undefined &&
      (obj.recentlyBookedHours = Math.round(message.recentlyBookedHours));
    message.interestCount !== undefined &&
      (obj.interestCount = Math.round(message.interestCount));
    message.recentBookingRegion !== undefined &&
      (obj.recentBookingRegion = message.recentBookingRegion);
    message.recentlyBookedHotel !== undefined &&
      (obj.recentlyBookedHotel = message.recentlyBookedHotel);
    return obj;
  },

  fromPartial(object: DeepPartial<Scarcity>): Scarcity {
    const message = createBaseScarcity();
    message.isPriceHammer = object.isPriceHammer ?? false;
    message.isTopCustomerRating = object.isTopCustomerRating ?? false;
    message.isBookedOut = object.isBookedOut ?? false;
    message.isBestSeller = object.isBestSeller ?? false;
    message.isHighlyDemanded = object.isHighlyDemanded ?? false;
    message.isFlexOptionAvailable = object.isFlexOptionAvailable ?? false;
    message.isTopLocation = object.isTopLocation ?? false;
    message.isCustomerRecommendation = object.isCustomerRecommendation ?? false;
    message.isRecentlyBooked = object.isRecentlyBooked ?? false;
    message.recentlyBookedHours = object.recentlyBookedHours ?? 0;
    message.interestCount = object.interestCount ?? 0;
    message.recentBookingRegion = object.recentBookingRegion ?? "";
    message.recentlyBookedHotel = object.recentlyBookedHotel ?? "";
    return message;
  },
};

function createBaseRating(): Rating {
  return {
    average: 0,
    count: 0,
    recommendation: 0,
    llmAggregatedSummary: "",
    ratingList: [],
    trustYouRatingList: [],
  };
}

export const Rating = {
  encode(
    message: Rating,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.average !== 0) {
      writer.uint32(13).float(message.average);
    }
    if (message.count !== 0) {
      writer.uint32(16).uint32(message.count);
    }
    if (message.recommendation !== 0) {
      writer.uint32(29).float(message.recommendation);
    }
    if (message.llmAggregatedSummary !== "") {
      writer.uint32(50).string(message.llmAggregatedSummary);
    }
    for (const v of message.ratingList) {
      UserRating.encode(v!, writer.uint32(34).fork()).ldelim();
    }
    for (const v of message.trustYouRatingList) {
      TrustYouRating.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Rating {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRating();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.average = reader.float();
          break;
        case 2:
          message.count = reader.uint32();
          break;
        case 3:
          message.recommendation = reader.float();
          break;
        case 6:
          message.llmAggregatedSummary = reader.string();
          break;
        case 4:
          message.ratingList.push(UserRating.decode(reader, reader.uint32()));
          break;
        case 5:
          message.trustYouRatingList.push(
            TrustYouRating.decode(reader, reader.uint32())
          );
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Rating {
    return {
      average: isSet(object.average) ? Number(object.average) : 0,
      count: isSet(object.count) ? Number(object.count) : 0,
      recommendation: isSet(object.recommendation)
        ? Number(object.recommendation)
        : 0,
      llmAggregatedSummary: isSet(object.llmAggregatedSummary)
        ? String(object.llmAggregatedSummary)
        : "",
      ratingList: Array.isArray(object?.ratingList)
        ? object.ratingList.map((e: any) => UserRating.fromJSON(e))
        : [],
      trustYouRatingList: Array.isArray(object?.trustYouRatingList)
        ? object.trustYouRatingList.map((e: any) => TrustYouRating.fromJSON(e))
        : [],
    };
  },

  toJSON(message: Rating): unknown {
    const obj: any = {};
    message.average !== undefined && (obj.average = message.average);
    message.count !== undefined && (obj.count = Math.round(message.count));
    message.recommendation !== undefined &&
      (obj.recommendation = message.recommendation);
    message.llmAggregatedSummary !== undefined &&
      (obj.llmAggregatedSummary = message.llmAggregatedSummary);
    if (message.ratingList) {
      obj.ratingList = message.ratingList.map((e) =>
        e ? UserRating.toJSON(e) : undefined
      );
    } else {
      obj.ratingList = [];
    }
    if (message.trustYouRatingList) {
      obj.trustYouRatingList = message.trustYouRatingList.map((e) =>
        e ? TrustYouRating.toJSON(e) : undefined
      );
    } else {
      obj.trustYouRatingList = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<Rating>): Rating {
    const message = createBaseRating();
    message.average = object.average ?? 0;
    message.count = object.count ?? 0;
    message.recommendation = object.recommendation ?? 0;
    message.llmAggregatedSummary = object.llmAggregatedSummary ?? "";
    message.ratingList =
      object.ratingList?.map((e) => UserRating.fromPartial(e)) || [];
    message.trustYouRatingList =
      object.trustYouRatingList?.map((e) => TrustYouRating.fromPartial(e)) ||
      [];
    return message;
  },
};

function createBaseFlags(): Flags {
  return {
    isFeaturedHotel: false,
    isHolidayHome: false,
    isAdultOnly: false,
    isBlackFridayDeal: false,
    hasStrikePrice: false,
    hasExclusiveBanner: false,
    offerLinkInSameTab: false,
    exclusiveBannerDescription: "",
    cancellationType: CancellationType.None,
  };
}

export const Flags = {
  encode(message: Flags, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.isFeaturedHotel === true) {
      writer.uint32(8).bool(message.isFeaturedHotel);
    }
    if (message.isHolidayHome === true) {
      writer.uint32(16).bool(message.isHolidayHome);
    }
    if (message.isAdultOnly === true) {
      writer.uint32(24).bool(message.isAdultOnly);
    }
    if (message.isBlackFridayDeal === true) {
      writer.uint32(40).bool(message.isBlackFridayDeal);
    }
    if (message.hasStrikePrice === true) {
      writer.uint32(48).bool(message.hasStrikePrice);
    }
    if (message.hasExclusiveBanner === true) {
      writer.uint32(56).bool(message.hasExclusiveBanner);
    }
    if (message.offerLinkInSameTab === true) {
      writer.uint32(72).bool(message.offerLinkInSameTab);
    }
    if (message.exclusiveBannerDescription !== "") {
      writer.uint32(34).string(message.exclusiveBannerDescription);
    }
    if (message.cancellationType !== CancellationType.None) {
      writer
        .uint32(80)
        .int32(cancellationTypeToNumber(message.cancellationType));
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Flags {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseFlags();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.isFeaturedHotel = reader.bool();
          break;
        case 2:
          message.isHolidayHome = reader.bool();
          break;
        case 3:
          message.isAdultOnly = reader.bool();
          break;
        case 5:
          message.isBlackFridayDeal = reader.bool();
          break;
        case 6:
          message.hasStrikePrice = reader.bool();
          break;
        case 7:
          message.hasExclusiveBanner = reader.bool();
          break;
        case 9:
          message.offerLinkInSameTab = reader.bool();
          break;
        case 4:
          message.exclusiveBannerDescription = reader.string();
          break;
        case 10:
          message.cancellationType = cancellationTypeFromJSON(reader.int32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Flags {
    return {
      isFeaturedHotel: isSet(object.isFeaturedHotel)
        ? Boolean(object.isFeaturedHotel)
        : false,
      isHolidayHome: isSet(object.isHolidayHome)
        ? Boolean(object.isHolidayHome)
        : false,
      isAdultOnly: isSet(object.isAdultOnly)
        ? Boolean(object.isAdultOnly)
        : false,
      isBlackFridayDeal: isSet(object.isBlackFridayDeal)
        ? Boolean(object.isBlackFridayDeal)
        : false,
      hasStrikePrice: isSet(object.hasStrikePrice)
        ? Boolean(object.hasStrikePrice)
        : false,
      hasExclusiveBanner: isSet(object.hasExclusiveBanner)
        ? Boolean(object.hasExclusiveBanner)
        : false,
      offerLinkInSameTab: isSet(object.offerLinkInSameTab)
        ? Boolean(object.offerLinkInSameTab)
        : false,
      exclusiveBannerDescription: isSet(object.exclusiveBannerDescription)
        ? String(object.exclusiveBannerDescription)
        : "",
      cancellationType: isSet(object.cancellationType)
        ? cancellationTypeFromJSON(object.cancellationType)
        : CancellationType.None,
    };
  },

  toJSON(message: Flags): unknown {
    const obj: any = {};
    message.isFeaturedHotel !== undefined &&
      (obj.isFeaturedHotel = message.isFeaturedHotel);
    message.isHolidayHome !== undefined &&
      (obj.isHolidayHome = message.isHolidayHome);
    message.isAdultOnly !== undefined &&
      (obj.isAdultOnly = message.isAdultOnly);
    message.isBlackFridayDeal !== undefined &&
      (obj.isBlackFridayDeal = message.isBlackFridayDeal);
    message.hasStrikePrice !== undefined &&
      (obj.hasStrikePrice = message.hasStrikePrice);
    message.hasExclusiveBanner !== undefined &&
      (obj.hasExclusiveBanner = message.hasExclusiveBanner);
    message.offerLinkInSameTab !== undefined &&
      (obj.offerLinkInSameTab = message.offerLinkInSameTab);
    message.exclusiveBannerDescription !== undefined &&
      (obj.exclusiveBannerDescription = message.exclusiveBannerDescription);
    message.cancellationType !== undefined &&
      (obj.cancellationType = cancellationTypeToJSON(message.cancellationType));
    return obj;
  },

  fromPartial(object: DeepPartial<Flags>): Flags {
    const message = createBaseFlags();
    message.isFeaturedHotel = object.isFeaturedHotel ?? false;
    message.isHolidayHome = object.isHolidayHome ?? false;
    message.isAdultOnly = object.isAdultOnly ?? false;
    message.isBlackFridayDeal = object.isBlackFridayDeal ?? false;
    message.hasStrikePrice = object.hasStrikePrice ?? false;
    message.hasExclusiveBanner = object.hasExclusiveBanner ?? false;
    message.offerLinkInSameTab = object.offerLinkInSameTab ?? false;
    message.exclusiveBannerDescription =
      object.exclusiveBannerDescription ?? "";
    message.cancellationType = object.cancellationType ?? CancellationType.None;
    return message;
  },
};

function createBaseLastVisited(): LastVisited {
  return { timestamp: 0, source: Device.DeviceUnknown };
}

export const LastVisited = {
  encode(
    message: LastVisited,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.timestamp !== 0) {
      writer.uint32(8).int32(message.timestamp);
    }
    if (message.source !== Device.DeviceUnknown) {
      writer.uint32(16).int32(deviceToNumber(message.source));
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LastVisited {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLastVisited();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.timestamp = reader.int32();
          break;
        case 2:
          message.source = deviceFromJSON(reader.int32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): LastVisited {
    return {
      timestamp: isSet(object.timestamp) ? Number(object.timestamp) : 0,
      source: isSet(object.source)
        ? deviceFromJSON(object.source)
        : Device.DeviceUnknown,
    };
  },

  toJSON(message: LastVisited): unknown {
    const obj: any = {};
    message.timestamp !== undefined &&
      (obj.timestamp = Math.round(message.timestamp));
    message.source !== undefined && (obj.source = deviceToJSON(message.source));
    return obj;
  },

  fromPartial(object: DeepPartial<LastVisited>): LastVisited {
    const message = createBaseLastVisited();
    message.timestamp = object.timestamp ?? 0;
    message.source = object.source ?? Device.DeviceUnknown;
    return message;
  },
};

function createBaseHotel(): Hotel {
  return {
    id: 0,
    recentBookingCount: 0,
    description: "",
    hibUrl: "",
    masterData: undefined,
    offer: undefined,
    flags: undefined,
    media: undefined,
    rating: undefined,
    distances: undefined,
    unitInformation: undefined,
    scarcity: undefined,
    tagManagerEvents: undefined,
    debugInformation: undefined,
    badgeList: undefined,
    rankedAttributeList: [],
    badgeItemList: [],
  };
}

export const Hotel = {
  encode(message: Hotel, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== 0) {
      writer.uint32(8).uint32(message.id);
    }
    if (message.recentBookingCount !== 0) {
      writer.uint32(120).uint32(message.recentBookingCount);
    }
    if (message.description !== "") {
      writer.uint32(138).string(message.description);
    }
    if (message.hibUrl !== "") {
      writer.uint32(146).string(message.hibUrl);
    }
    if (message.masterData !== undefined) {
      MasterData.encode(message.masterData, writer.uint32(18).fork()).ldelim();
    }
    if (message.offer !== undefined) {
      Offer.encode(message.offer, writer.uint32(26).fork()).ldelim();
    }
    if (message.flags !== undefined) {
      Flags.encode(message.flags, writer.uint32(34).fork()).ldelim();
    }
    if (message.media !== undefined) {
      Hotel_Media.encode(message.media, writer.uint32(42).fork()).ldelim();
    }
    if (message.rating !== undefined) {
      Rating.encode(message.rating, writer.uint32(50).fork()).ldelim();
    }
    if (message.distances !== undefined) {
      Distances.encode(message.distances, writer.uint32(58).fork()).ldelim();
    }
    if (message.unitInformation !== undefined) {
      UnitInformation.encode(
        message.unitInformation,
        writer.uint32(66).fork()
      ).ldelim();
    }
    if (message.scarcity !== undefined) {
      Scarcity.encode(message.scarcity, writer.uint32(82).fork()).ldelim();
    }
    if (message.tagManagerEvents !== undefined) {
      GoogleTagManagerEvents.encode(
        message.tagManagerEvents,
        writer.uint32(98).fork()
      ).ldelim();
    }
    if (message.debugInformation !== undefined) {
      DebugInformation.encode(
        message.debugInformation,
        writer.uint32(106).fork()
      ).ldelim();
    }
    if (message.badgeList !== undefined) {
      Hotel_BadgeList.encode(
        message.badgeList,
        writer.uint32(114).fork()
      ).ldelim();
    }
    writer.uint32(130).fork();
    for (const v of message.rankedAttributeList) {
      writer.int32(attributeToNumber(v));
    }
    writer.ldelim();
    for (const v of message.badgeItemList) {
      Hotel_Badge.encode(v!, writer.uint32(154).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Hotel {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHotel();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.id = reader.uint32();
          break;
        case 15:
          message.recentBookingCount = reader.uint32();
          break;
        case 17:
          message.description = reader.string();
          break;
        case 18:
          message.hibUrl = reader.string();
          break;
        case 2:
          message.masterData = MasterData.decode(reader, reader.uint32());
          break;
        case 3:
          message.offer = Offer.decode(reader, reader.uint32());
          break;
        case 4:
          message.flags = Flags.decode(reader, reader.uint32());
          break;
        case 5:
          message.media = Hotel_Media.decode(reader, reader.uint32());
          break;
        case 6:
          message.rating = Rating.decode(reader, reader.uint32());
          break;
        case 7:
          message.distances = Distances.decode(reader, reader.uint32());
          break;
        case 8:
          message.unitInformation = UnitInformation.decode(
            reader,
            reader.uint32()
          );
          break;
        case 10:
          message.scarcity = Scarcity.decode(reader, reader.uint32());
          break;
        case 12:
          message.tagManagerEvents = GoogleTagManagerEvents.decode(
            reader,
            reader.uint32()
          );
          break;
        case 13:
          message.debugInformation = DebugInformation.decode(
            reader,
            reader.uint32()
          );
          break;
        case 14:
          message.badgeList = Hotel_BadgeList.decode(reader, reader.uint32());
          break;
        case 16:
          if ((tag & 7) === 2) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.rankedAttributeList.push(
                attributeFromJSON(reader.int32())
              );
            }
          } else {
            message.rankedAttributeList.push(attributeFromJSON(reader.int32()));
          }
          break;
        case 19:
          message.badgeItemList.push(
            Hotel_Badge.decode(reader, reader.uint32())
          );
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Hotel {
    return {
      id: isSet(object.id) ? Number(object.id) : 0,
      recentBookingCount: isSet(object.recentBookingCount)
        ? Number(object.recentBookingCount)
        : 0,
      description: isSet(object.description) ? String(object.description) : "",
      hibUrl: isSet(object.hibUrl) ? String(object.hibUrl) : "",
      masterData: isSet(object.masterData)
        ? MasterData.fromJSON(object.masterData)
        : undefined,
      offer: isSet(object.offer) ? Offer.fromJSON(object.offer) : undefined,
      flags: isSet(object.flags) ? Flags.fromJSON(object.flags) : undefined,
      media: isSet(object.media)
        ? Hotel_Media.fromJSON(object.media)
        : undefined,
      rating: isSet(object.rating) ? Rating.fromJSON(object.rating) : undefined,
      distances: isSet(object.distances)
        ? Distances.fromJSON(object.distances)
        : undefined,
      unitInformation: isSet(object.unitInformation)
        ? UnitInformation.fromJSON(object.unitInformation)
        : undefined,
      scarcity: isSet(object.scarcity)
        ? Scarcity.fromJSON(object.scarcity)
        : undefined,
      tagManagerEvents: isSet(object.tagManagerEvents)
        ? GoogleTagManagerEvents.fromJSON(object.tagManagerEvents)
        : undefined,
      debugInformation: isSet(object.debugInformation)
        ? DebugInformation.fromJSON(object.debugInformation)
        : undefined,
      badgeList: isSet(object.badgeList)
        ? Hotel_BadgeList.fromJSON(object.badgeList)
        : undefined,
      rankedAttributeList: Array.isArray(object?.rankedAttributeList)
        ? object.rankedAttributeList.map((e: any) => attributeFromJSON(e))
        : [],
      badgeItemList: Array.isArray(object?.badgeItemList)
        ? object.badgeItemList.map((e: any) => Hotel_Badge.fromJSON(e))
        : [],
    };
  },

  toJSON(message: Hotel): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = Math.round(message.id));
    message.recentBookingCount !== undefined &&
      (obj.recentBookingCount = Math.round(message.recentBookingCount));
    message.description !== undefined &&
      (obj.description = message.description);
    message.hibUrl !== undefined && (obj.hibUrl = message.hibUrl);
    message.masterData !== undefined &&
      (obj.masterData = message.masterData
        ? MasterData.toJSON(message.masterData)
        : undefined);
    message.offer !== undefined &&
      (obj.offer = message.offer ? Offer.toJSON(message.offer) : undefined);
    message.flags !== undefined &&
      (obj.flags = message.flags ? Flags.toJSON(message.flags) : undefined);
    message.media !== undefined &&
      (obj.media = message.media
        ? Hotel_Media.toJSON(message.media)
        : undefined);
    message.rating !== undefined &&
      (obj.rating = message.rating ? Rating.toJSON(message.rating) : undefined);
    message.distances !== undefined &&
      (obj.distances = message.distances
        ? Distances.toJSON(message.distances)
        : undefined);
    message.unitInformation !== undefined &&
      (obj.unitInformation = message.unitInformation
        ? UnitInformation.toJSON(message.unitInformation)
        : undefined);
    message.scarcity !== undefined &&
      (obj.scarcity = message.scarcity
        ? Scarcity.toJSON(message.scarcity)
        : undefined);
    message.tagManagerEvents !== undefined &&
      (obj.tagManagerEvents = message.tagManagerEvents
        ? GoogleTagManagerEvents.toJSON(message.tagManagerEvents)
        : undefined);
    message.debugInformation !== undefined &&
      (obj.debugInformation = message.debugInformation
        ? DebugInformation.toJSON(message.debugInformation)
        : undefined);
    message.badgeList !== undefined &&
      (obj.badgeList = message.badgeList
        ? Hotel_BadgeList.toJSON(message.badgeList)
        : undefined);
    if (message.rankedAttributeList) {
      obj.rankedAttributeList = message.rankedAttributeList.map((e) =>
        attributeToJSON(e)
      );
    } else {
      obj.rankedAttributeList = [];
    }
    if (message.badgeItemList) {
      obj.badgeItemList = message.badgeItemList.map((e) =>
        e ? Hotel_Badge.toJSON(e) : undefined
      );
    } else {
      obj.badgeItemList = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<Hotel>): Hotel {
    const message = createBaseHotel();
    message.id = object.id ?? 0;
    message.recentBookingCount = object.recentBookingCount ?? 0;
    message.description = object.description ?? "";
    message.hibUrl = object.hibUrl ?? "";
    message.masterData =
      object.masterData !== undefined && object.masterData !== null
        ? MasterData.fromPartial(object.masterData)
        : undefined;
    message.offer =
      object.offer !== undefined && object.offer !== null
        ? Offer.fromPartial(object.offer)
        : undefined;
    message.flags =
      object.flags !== undefined && object.flags !== null
        ? Flags.fromPartial(object.flags)
        : undefined;
    message.media =
      object.media !== undefined && object.media !== null
        ? Hotel_Media.fromPartial(object.media)
        : undefined;
    message.rating =
      object.rating !== undefined && object.rating !== null
        ? Rating.fromPartial(object.rating)
        : undefined;
    message.distances =
      object.distances !== undefined && object.distances !== null
        ? Distances.fromPartial(object.distances)
        : undefined;
    message.unitInformation =
      object.unitInformation !== undefined && object.unitInformation !== null
        ? UnitInformation.fromPartial(object.unitInformation)
        : undefined;
    message.scarcity =
      object.scarcity !== undefined && object.scarcity !== null
        ? Scarcity.fromPartial(object.scarcity)
        : undefined;
    message.tagManagerEvents =
      object.tagManagerEvents !== undefined && object.tagManagerEvents !== null
        ? GoogleTagManagerEvents.fromPartial(object.tagManagerEvents)
        : undefined;
    message.debugInformation =
      object.debugInformation !== undefined && object.debugInformation !== null
        ? DebugInformation.fromPartial(object.debugInformation)
        : undefined;
    message.badgeList =
      object.badgeList !== undefined && object.badgeList !== null
        ? Hotel_BadgeList.fromPartial(object.badgeList)
        : undefined;
    message.rankedAttributeList =
      object.rankedAttributeList?.map((e) => e) || [];
    message.badgeItemList =
      object.badgeItemList?.map((e) => Hotel_Badge.fromPartial(e)) || [];
    return message;
  },
};

function createBaseHotel_Media(): Hotel_Media {
  return {
    hasVideo: false,
    totalImageCount: 0,
    imageCount: 0,
    guestImageCount: 0,
    images: [],
  };
}

export const Hotel_Media = {
  encode(
    message: Hotel_Media,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.hasVideo === true) {
      writer.uint32(16).bool(message.hasVideo);
    }
    if (message.totalImageCount !== 0) {
      writer.uint32(24).uint32(message.totalImageCount);
    }
    if (message.imageCount !== 0) {
      writer.uint32(32).uint32(message.imageCount);
    }
    if (message.guestImageCount !== 0) {
      writer.uint32(40).uint32(message.guestImageCount);
    }
    for (const v of message.images) {
      Image.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Hotel_Media {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHotel_Media();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2:
          message.hasVideo = reader.bool();
          break;
        case 3:
          message.totalImageCount = reader.uint32();
          break;
        case 4:
          message.imageCount = reader.uint32();
          break;
        case 5:
          message.guestImageCount = reader.uint32();
          break;
        case 1:
          message.images.push(Image.decode(reader, reader.uint32()));
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Hotel_Media {
    return {
      hasVideo: isSet(object.hasVideo) ? Boolean(object.hasVideo) : false,
      totalImageCount: isSet(object.totalImageCount)
        ? Number(object.totalImageCount)
        : 0,
      imageCount: isSet(object.imageCount) ? Number(object.imageCount) : 0,
      guestImageCount: isSet(object.guestImageCount)
        ? Number(object.guestImageCount)
        : 0,
      images: Array.isArray(object?.images)
        ? object.images.map((e: any) => Image.fromJSON(e))
        : [],
    };
  },

  toJSON(message: Hotel_Media): unknown {
    const obj: any = {};
    message.hasVideo !== undefined && (obj.hasVideo = message.hasVideo);
    message.totalImageCount !== undefined &&
      (obj.totalImageCount = Math.round(message.totalImageCount));
    message.imageCount !== undefined &&
      (obj.imageCount = Math.round(message.imageCount));
    message.guestImageCount !== undefined &&
      (obj.guestImageCount = Math.round(message.guestImageCount));
    if (message.images) {
      obj.images = message.images.map((e) => (e ? Image.toJSON(e) : undefined));
    } else {
      obj.images = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<Hotel_Media>): Hotel_Media {
    const message = createBaseHotel_Media();
    message.hasVideo = object.hasVideo ?? false;
    message.totalImageCount = object.totalImageCount ?? 0;
    message.imageCount = object.imageCount ?? 0;
    message.guestImageCount = object.guestImageCount ?? 0;
    message.images = object.images?.map((e) => Image.fromPartial(e)) || [];
    return message;
  },
};

function createBaseHotel_BadgeList(): Hotel_BadgeList {
  return { irBadgeList: [] };
}

export const Hotel_BadgeList = {
  encode(
    message: Hotel_BadgeList,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    for (const v of message.irBadgeList) {
      Hotel_Badge.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Hotel_BadgeList {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHotel_BadgeList();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.irBadgeList.push(Hotel_Badge.decode(reader, reader.uint32()));
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Hotel_BadgeList {
    return {
      irBadgeList: Array.isArray(object?.irBadgeList)
        ? object.irBadgeList.map((e: any) => Hotel_Badge.fromJSON(e))
        : [],
    };
  },

  toJSON(message: Hotel_BadgeList): unknown {
    const obj: any = {};
    if (message.irBadgeList) {
      obj.irBadgeList = message.irBadgeList.map((e) =>
        e ? Hotel_Badge.toJSON(e) : undefined
      );
    } else {
      obj.irBadgeList = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<Hotel_BadgeList>): Hotel_BadgeList {
    const message = createBaseHotel_BadgeList();
    message.irBadgeList =
      object.irBadgeList?.map((e) => Hotel_Badge.fromPartial(e)) || [];
    return message;
  },
};

function createBaseHotel_Badge(): Hotel_Badge {
  return {
    title: "",
    description: "",
    params: {},
    type: Hotel_Badge_Type.Unknown,
  };
}

export const Hotel_Badge = {
  encode(
    message: Hotel_Badge,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.title !== "") {
      writer.uint32(10).string(message.title);
    }
    if (message.description !== "") {
      writer.uint32(18).string(message.description);
    }
    Object.entries(message.params).forEach(([key, value]) => {
      Hotel_Badge_ParamsEntry.encode(
        { key: key as any, value },
        writer.uint32(26).fork()
      ).ldelim();
    });
    if (message.type !== Hotel_Badge_Type.Unknown) {
      writer.uint32(32).int32(hotel_Badge_TypeToNumber(message.type));
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Hotel_Badge {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHotel_Badge();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.title = reader.string();
          break;
        case 2:
          message.description = reader.string();
          break;
        case 3:
          const entry3 = Hotel_Badge_ParamsEntry.decode(
            reader,
            reader.uint32()
          );
          if (entry3.value !== undefined) {
            message.params[entry3.key] = entry3.value;
          }
          break;
        case 4:
          message.type = hotel_Badge_TypeFromJSON(reader.int32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Hotel_Badge {
    return {
      title: isSet(object.title) ? String(object.title) : "",
      description: isSet(object.description) ? String(object.description) : "",
      params: isObject(object.params)
        ? Object.entries(object.params).reduce<{ [key: string]: string }>(
            (acc, [key, value]) => {
              acc[key] = String(value);
              return acc;
            },
            {}
          )
        : {},
      type: isSet(object.type)
        ? hotel_Badge_TypeFromJSON(object.type)
        : Hotel_Badge_Type.Unknown,
    };
  },

  toJSON(message: Hotel_Badge): unknown {
    const obj: any = {};
    message.title !== undefined && (obj.title = message.title);
    message.description !== undefined &&
      (obj.description = message.description);
    obj.params = {};
    if (message.params) {
      Object.entries(message.params).forEach(([k, v]) => {
        obj.params[k] = v;
      });
    }
    message.type !== undefined &&
      (obj.type = hotel_Badge_TypeToJSON(message.type));
    return obj;
  },

  fromPartial(object: DeepPartial<Hotel_Badge>): Hotel_Badge {
    const message = createBaseHotel_Badge();
    message.title = object.title ?? "";
    message.description = object.description ?? "";
    message.params = Object.entries(object.params ?? {}).reduce<{
      [key: string]: string;
    }>((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = String(value);
      }
      return acc;
    }, {});
    message.type = object.type ?? Hotel_Badge_Type.Unknown;
    return message;
  },
};

function createBaseHotel_Badge_ParamsEntry(): Hotel_Badge_ParamsEntry {
  return { key: "", value: "" };
}

export const Hotel_Badge_ParamsEntry = {
  encode(
    message: Hotel_Badge_ParamsEntry,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): Hotel_Badge_ParamsEntry {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHotel_Badge_ParamsEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.key = reader.string();
          break;
        case 2:
          message.value = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Hotel_Badge_ParamsEntry {
    return {
      key: isSet(object.key) ? String(object.key) : "",
      value: isSet(object.value) ? String(object.value) : "",
    };
  },

  toJSON(message: Hotel_Badge_ParamsEntry): unknown {
    const obj: any = {};
    message.key !== undefined && (obj.key = message.key);
    message.value !== undefined && (obj.value = message.value);
    return obj;
  },

  fromPartial(
    object: DeepPartial<Hotel_Badge_ParamsEntry>
  ): Hotel_Badge_ParamsEntry {
    const message = createBaseHotel_Badge_ParamsEntry();
    message.key = object.key ?? "";
    message.value = object.value ?? "";
    return message;
  },
};

function createBaseTracking(): Tracking {
  return { impressions: [] };
}

export const Tracking = {
  encode(
    message: Tracking,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    for (const v of message.impressions) {
      writer.uint32(10).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Tracking {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseTracking();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.impressions.push(reader.string());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Tracking {
    return {
      impressions: Array.isArray(object?.impressions)
        ? object.impressions.map((e: any) => String(e))
        : [],
    };
  },

  toJSON(message: Tracking): unknown {
    const obj: any = {};
    if (message.impressions) {
      obj.impressions = message.impressions.map((e) => e);
    } else {
      obj.impressions = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<Tracking>): Tracking {
    const message = createBaseTracking();
    message.impressions = object.impressions?.map((e) => e) || [];
    return message;
  },
};

function createBaseMismatchFilter(): MismatchFilter {
  return { name: "", value: "" };
}

export const MismatchFilter = {
  encode(
    message: MismatchFilter,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.name !== "") {
      writer.uint32(10).string(message.name);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MismatchFilter {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMismatchFilter();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.name = reader.string();
          break;
        case 2:
          message.value = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MismatchFilter {
    return {
      name: isSet(object.name) ? String(object.name) : "",
      value: isSet(object.value) ? String(object.value) : "",
    };
  },

  toJSON(message: MismatchFilter): unknown {
    const obj: any = {};
    message.name !== undefined && (obj.name = message.name);
    message.value !== undefined && (obj.value = message.value);
    return obj;
  },

  fromPartial(object: DeepPartial<MismatchFilter>): MismatchFilter {
    const message = createBaseMismatchFilter();
    message.name = object.name ?? "";
    message.value = object.value ?? "";
    return message;
  },
};

function createBaseMetaData(): MetaData {
  return {
    withChildren: false,
    travellerCount: 0,
    redirectUrl: "",
    autoCompleterDestinationName: "",
    defaultOfferLink: "",
    tracking: undefined,
    voucher: undefined,
    scarcity: undefined,
    historyData: undefined,
    featuredMismatchFilterList: [],
    featuredAlternativeList: [],
  };
}

export const MetaData = {
  encode(
    message: MetaData,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.withChildren === true) {
      writer.uint32(32).bool(message.withChildren);
    }
    if (message.travellerCount !== 0) {
      writer.uint32(24).uint32(message.travellerCount);
    }
    if (message.redirectUrl !== "") {
      writer.uint32(82).string(message.redirectUrl);
    }
    if (message.autoCompleterDestinationName !== "") {
      writer.uint32(90).string(message.autoCompleterDestinationName);
    }
    if (message.defaultOfferLink !== "") {
      writer.uint32(98).string(message.defaultOfferLink);
    }
    if (message.tracking !== undefined) {
      Tracking.encode(message.tracking, writer.uint32(10).fork()).ldelim();
    }
    if (message.voucher !== undefined) {
      MetaData_Voucher.encode(
        message.voucher,
        writer.uint32(50).fork()
      ).ldelim();
    }
    if (message.scarcity !== undefined) {
      MetaData_Scarcity.encode(
        message.scarcity,
        writer.uint32(58).fork()
      ).ldelim();
    }
    if (message.historyData !== undefined) {
      MetaData_HistoryData.encode(
        message.historyData,
        writer.uint32(66).fork()
      ).ldelim();
    }
    for (const v of message.featuredMismatchFilterList) {
      MismatchFilter.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    for (const v of message.featuredAlternativeList) {
      FeaturedAlternative.encode(v!, writer.uint32(74).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MetaData {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMetaData();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 4:
          message.withChildren = reader.bool();
          break;
        case 3:
          message.travellerCount = reader.uint32();
          break;
        case 10:
          message.redirectUrl = reader.string();
          break;
        case 11:
          message.autoCompleterDestinationName = reader.string();
          break;
        case 12:
          message.defaultOfferLink = reader.string();
          break;
        case 1:
          message.tracking = Tracking.decode(reader, reader.uint32());
          break;
        case 6:
          message.voucher = MetaData_Voucher.decode(reader, reader.uint32());
          break;
        case 7:
          message.scarcity = MetaData_Scarcity.decode(reader, reader.uint32());
          break;
        case 8:
          message.historyData = MetaData_HistoryData.decode(
            reader,
            reader.uint32()
          );
          break;
        case 5:
          message.featuredMismatchFilterList.push(
            MismatchFilter.decode(reader, reader.uint32())
          );
          break;
        case 9:
          message.featuredAlternativeList.push(
            FeaturedAlternative.decode(reader, reader.uint32())
          );
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MetaData {
    return {
      withChildren: isSet(object.withChildren)
        ? Boolean(object.withChildren)
        : false,
      travellerCount: isSet(object.travellerCount)
        ? Number(object.travellerCount)
        : 0,
      redirectUrl: isSet(object.redirectUrl) ? String(object.redirectUrl) : "",
      autoCompleterDestinationName: isSet(object.autoCompleterDestinationName)
        ? String(object.autoCompleterDestinationName)
        : "",
      defaultOfferLink: isSet(object.defaultOfferLink)
        ? String(object.defaultOfferLink)
        : "",
      tracking: isSet(object.tracking)
        ? Tracking.fromJSON(object.tracking)
        : undefined,
      voucher: isSet(object.voucher)
        ? MetaData_Voucher.fromJSON(object.voucher)
        : undefined,
      scarcity: isSet(object.scarcity)
        ? MetaData_Scarcity.fromJSON(object.scarcity)
        : undefined,
      historyData: isSet(object.historyData)
        ? MetaData_HistoryData.fromJSON(object.historyData)
        : undefined,
      featuredMismatchFilterList: Array.isArray(
        object?.featuredMismatchFilterList
      )
        ? object.featuredMismatchFilterList.map((e: any) =>
            MismatchFilter.fromJSON(e)
          )
        : [],
      featuredAlternativeList: Array.isArray(object?.featuredAlternativeList)
        ? object.featuredAlternativeList.map((e: any) =>
            FeaturedAlternative.fromJSON(e)
          )
        : [],
    };
  },

  toJSON(message: MetaData): unknown {
    const obj: any = {};
    message.withChildren !== undefined &&
      (obj.withChildren = message.withChildren);
    message.travellerCount !== undefined &&
      (obj.travellerCount = Math.round(message.travellerCount));
    message.redirectUrl !== undefined &&
      (obj.redirectUrl = message.redirectUrl);
    message.autoCompleterDestinationName !== undefined &&
      (obj.autoCompleterDestinationName = message.autoCompleterDestinationName);
    message.defaultOfferLink !== undefined &&
      (obj.defaultOfferLink = message.defaultOfferLink);
    message.tracking !== undefined &&
      (obj.tracking = message.tracking
        ? Tracking.toJSON(message.tracking)
        : undefined);
    message.voucher !== undefined &&
      (obj.voucher = message.voucher
        ? MetaData_Voucher.toJSON(message.voucher)
        : undefined);
    message.scarcity !== undefined &&
      (obj.scarcity = message.scarcity
        ? MetaData_Scarcity.toJSON(message.scarcity)
        : undefined);
    message.historyData !== undefined &&
      (obj.historyData = message.historyData
        ? MetaData_HistoryData.toJSON(message.historyData)
        : undefined);
    if (message.featuredMismatchFilterList) {
      obj.featuredMismatchFilterList = message.featuredMismatchFilterList.map(
        (e) => (e ? MismatchFilter.toJSON(e) : undefined)
      );
    } else {
      obj.featuredMismatchFilterList = [];
    }
    if (message.featuredAlternativeList) {
      obj.featuredAlternativeList = message.featuredAlternativeList.map((e) =>
        e ? FeaturedAlternative.toJSON(e) : undefined
      );
    } else {
      obj.featuredAlternativeList = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<MetaData>): MetaData {
    const message = createBaseMetaData();
    message.withChildren = object.withChildren ?? false;
    message.travellerCount = object.travellerCount ?? 0;
    message.redirectUrl = object.redirectUrl ?? "";
    message.autoCompleterDestinationName =
      object.autoCompleterDestinationName ?? "";
    message.defaultOfferLink = object.defaultOfferLink ?? "";
    message.tracking =
      object.tracking !== undefined && object.tracking !== null
        ? Tracking.fromPartial(object.tracking)
        : undefined;
    message.voucher =
      object.voucher !== undefined && object.voucher !== null
        ? MetaData_Voucher.fromPartial(object.voucher)
        : undefined;
    message.scarcity =
      object.scarcity !== undefined && object.scarcity !== null
        ? MetaData_Scarcity.fromPartial(object.scarcity)
        : undefined;
    message.historyData =
      object.historyData !== undefined && object.historyData !== null
        ? MetaData_HistoryData.fromPartial(object.historyData)
        : undefined;
    message.featuredMismatchFilterList =
      object.featuredMismatchFilterList?.map((e) =>
        MismatchFilter.fromPartial(e)
      ) || [];
    message.featuredAlternativeList =
      object.featuredAlternativeList?.map((e) =>
        FeaturedAlternative.fromPartial(e)
      ) || [];
    return message;
  },
};

function createBaseMetaData_Voucher(): MetaData_Voucher {
  return {
    minPrice: 0,
    additionalAppRedeemAmount: 0,
    conditionsUrl: "",
    priceSteps: [],
  };
}

export const MetaData_Voucher = {
  encode(
    message: MetaData_Voucher,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.minPrice !== 0) {
      writer.uint32(8).uint32(message.minPrice);
    }
    if (message.additionalAppRedeemAmount !== 0) {
      writer.uint32(32).uint32(message.additionalAppRedeemAmount);
    }
    if (message.conditionsUrl !== "") {
      writer.uint32(18).string(message.conditionsUrl);
    }
    for (const v of message.priceSteps) {
      PriceStep.encode(v!, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MetaData_Voucher {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMetaData_Voucher();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.minPrice = reader.uint32();
          break;
        case 4:
          message.additionalAppRedeemAmount = reader.uint32();
          break;
        case 2:
          message.conditionsUrl = reader.string();
          break;
        case 3:
          message.priceSteps.push(PriceStep.decode(reader, reader.uint32()));
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MetaData_Voucher {
    return {
      minPrice: isSet(object.minPrice) ? Number(object.minPrice) : 0,
      additionalAppRedeemAmount: isSet(object.additionalAppRedeemAmount)
        ? Number(object.additionalAppRedeemAmount)
        : 0,
      conditionsUrl: isSet(object.conditionsUrl)
        ? String(object.conditionsUrl)
        : "",
      priceSteps: Array.isArray(object?.priceSteps)
        ? object.priceSteps.map((e: any) => PriceStep.fromJSON(e))
        : [],
    };
  },

  toJSON(message: MetaData_Voucher): unknown {
    const obj: any = {};
    message.minPrice !== undefined &&
      (obj.minPrice = Math.round(message.minPrice));
    message.additionalAppRedeemAmount !== undefined &&
      (obj.additionalAppRedeemAmount = Math.round(
        message.additionalAppRedeemAmount
      ));
    message.conditionsUrl !== undefined &&
      (obj.conditionsUrl = message.conditionsUrl);
    if (message.priceSteps) {
      obj.priceSteps = message.priceSteps.map((e) =>
        e ? PriceStep.toJSON(e) : undefined
      );
    } else {
      obj.priceSteps = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<MetaData_Voucher>): MetaData_Voucher {
    const message = createBaseMetaData_Voucher();
    message.minPrice = object.minPrice ?? 0;
    message.additionalAppRedeemAmount = object.additionalAppRedeemAmount ?? 0;
    message.conditionsUrl = object.conditionsUrl ?? "";
    message.priceSteps =
      object.priceSteps?.map((e) => PriceStep.fromPartial(e)) || [];
    return message;
  },
};

function createBaseMetaData_Scarcity(): MetaData_Scarcity {
  return { highlyDemanded: false, recentBookingRegion: "" };
}

export const MetaData_Scarcity = {
  encode(
    message: MetaData_Scarcity,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.highlyDemanded === true) {
      writer.uint32(8).bool(message.highlyDemanded);
    }
    if (message.recentBookingRegion !== "") {
      writer.uint32(18).string(message.recentBookingRegion);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MetaData_Scarcity {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMetaData_Scarcity();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.highlyDemanded = reader.bool();
          break;
        case 2:
          message.recentBookingRegion = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MetaData_Scarcity {
    return {
      highlyDemanded: isSet(object.highlyDemanded)
        ? Boolean(object.highlyDemanded)
        : false,
      recentBookingRegion: isSet(object.recentBookingRegion)
        ? String(object.recentBookingRegion)
        : "",
    };
  },

  toJSON(message: MetaData_Scarcity): unknown {
    const obj: any = {};
    message.highlyDemanded !== undefined &&
      (obj.highlyDemanded = message.highlyDemanded);
    message.recentBookingRegion !== undefined &&
      (obj.recentBookingRegion = message.recentBookingRegion);
    return obj;
  },

  fromPartial(object: DeepPartial<MetaData_Scarcity>): MetaData_Scarcity {
    const message = createBaseMetaData_Scarcity();
    message.highlyDemanded = object.highlyDemanded ?? false;
    message.recentBookingRegion = object.recentBookingRegion ?? "";
    return message;
  },
};

function createBaseMetaData_HistoryData(): MetaData_HistoryData {
  return { imageUrl: "", addItem: undefined };
}

export const MetaData_HistoryData = {
  encode(
    message: MetaData_HistoryData,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.imageUrl !== "") {
      writer.uint32(10).string(message.imageUrl);
    }
    if (message.addItem !== undefined) {
      MetaData_HistoryData_Item.encode(
        message.addItem,
        writer.uint32(18).fork()
      ).ldelim();
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): MetaData_HistoryData {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMetaData_HistoryData();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.imageUrl = reader.string();
          break;
        case 2:
          message.addItem = MetaData_HistoryData_Item.decode(
            reader,
            reader.uint32()
          );
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MetaData_HistoryData {
    return {
      imageUrl: isSet(object.imageUrl) ? String(object.imageUrl) : "",
      addItem: isSet(object.addItem)
        ? MetaData_HistoryData_Item.fromJSON(object.addItem)
        : undefined,
    };
  },

  toJSON(message: MetaData_HistoryData): unknown {
    const obj: any = {};
    message.imageUrl !== undefined && (obj.imageUrl = message.imageUrl);
    message.addItem !== undefined &&
      (obj.addItem = message.addItem
        ? MetaData_HistoryData_Item.toJSON(message.addItem)
        : undefined);
    return obj;
  },

  fromPartial(object: DeepPartial<MetaData_HistoryData>): MetaData_HistoryData {
    const message = createBaseMetaData_HistoryData();
    message.imageUrl = object.imageUrl ?? "";
    message.addItem =
      object.addItem !== undefined && object.addItem !== null
        ? MetaData_HistoryData_Item.fromPartial(object.addItem)
        : undefined;
    return message;
  },
};

function createBaseMetaData_HistoryData_Item(): MetaData_HistoryData_Item {
  return { id: "", url: "", price: 0, regionName: "" };
}

export const MetaData_HistoryData_Item = {
  encode(
    message: MetaData_HistoryData_Item,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.url !== "") {
      writer.uint32(18).string(message.url);
    }
    if (message.price !== 0) {
      writer.uint32(29).float(message.price);
    }
    if (message.regionName !== "") {
      writer.uint32(34).string(message.regionName);
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): MetaData_HistoryData_Item {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMetaData_HistoryData_Item();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.id = reader.string();
          break;
        case 2:
          message.url = reader.string();
          break;
        case 3:
          message.price = reader.float();
          break;
        case 4:
          message.regionName = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): MetaData_HistoryData_Item {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      url: isSet(object.url) ? String(object.url) : "",
      price: isSet(object.price) ? Number(object.price) : 0,
      regionName: isSet(object.regionName) ? String(object.regionName) : "",
    };
  },

  toJSON(message: MetaData_HistoryData_Item): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.url !== undefined && (obj.url = message.url);
    message.price !== undefined && (obj.price = message.price);
    message.regionName !== undefined && (obj.regionName = message.regionName);
    return obj;
  },

  fromPartial(
    object: DeepPartial<MetaData_HistoryData_Item>
  ): MetaData_HistoryData_Item {
    const message = createBaseMetaData_HistoryData_Item();
    message.id = object.id ?? "";
    message.url = object.url ?? "";
    message.price = object.price ?? 0;
    message.regionName = object.regionName ?? "";
    return message;
  },
};

function createBaseHotelList(): HotelList {
  return {
    count: 0,
    priceMin: 0,
    priceMax: 0,
    pollUrl: "",
    itemOrder: [],
    items: {},
    meta: undefined,
  };
}

export const HotelList = {
  encode(
    message: HotelList,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.count !== 0) {
      writer.uint32(24).uint32(message.count);
    }
    if (message.priceMin !== 0) {
      writer.uint32(37).float(message.priceMin);
    }
    if (message.priceMax !== 0) {
      writer.uint32(45).float(message.priceMax);
    }
    if (message.pollUrl !== "") {
      writer.uint32(50).string(message.pollUrl);
    }
    writer.uint32(18).fork();
    for (const v of message.itemOrder) {
      writer.uint32(v);
    }
    writer.ldelim();
    Object.entries(message.items).forEach(([key, value]) => {
      HotelList_ItemsEntry.encode(
        { key: key as any, value },
        writer.uint32(10).fork()
      ).ldelim();
    });
    if (message.meta !== undefined) {
      MetaData.encode(message.meta, writer.uint32(58).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): HotelList {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHotelList();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 3:
          message.count = reader.uint32();
          break;
        case 4:
          message.priceMin = reader.float();
          break;
        case 5:
          message.priceMax = reader.float();
          break;
        case 6:
          message.pollUrl = reader.string();
          break;
        case 2:
          if ((tag & 7) === 2) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.itemOrder.push(reader.uint32());
            }
          } else {
            message.itemOrder.push(reader.uint32());
          }
          break;
        case 1:
          const entry1 = HotelList_ItemsEntry.decode(reader, reader.uint32());
          if (entry1.value !== undefined) {
            message.items[entry1.key] = entry1.value;
          }
          break;
        case 7:
          message.meta = MetaData.decode(reader, reader.uint32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): HotelList {
    return {
      count: isSet(object.count) ? Number(object.count) : 0,
      priceMin: isSet(object.priceMin) ? Number(object.priceMin) : 0,
      priceMax: isSet(object.priceMax) ? Number(object.priceMax) : 0,
      pollUrl: isSet(object.pollUrl) ? String(object.pollUrl) : "",
      itemOrder: Array.isArray(object?.itemOrder)
        ? object.itemOrder.map((e: any) => Number(e))
        : [],
      items: isObject(object.items)
        ? Object.entries(object.items).reduce<{ [key: string]: Hotel }>(
            (acc, [key, value]) => {
              acc[key] = Hotel.fromJSON(value);
              return acc;
            },
            {}
          )
        : {},
      meta: isSet(object.meta) ? MetaData.fromJSON(object.meta) : undefined,
    };
  },

  toJSON(message: HotelList): unknown {
    const obj: any = {};
    message.count !== undefined && (obj.count = Math.round(message.count));
    message.priceMin !== undefined && (obj.priceMin = message.priceMin);
    message.priceMax !== undefined && (obj.priceMax = message.priceMax);
    message.pollUrl !== undefined && (obj.pollUrl = message.pollUrl);
    if (message.itemOrder) {
      obj.itemOrder = message.itemOrder.map((e) => Math.round(e));
    } else {
      obj.itemOrder = [];
    }
    obj.items = {};
    if (message.items) {
      Object.entries(message.items).forEach(([k, v]) => {
        obj.items[k] = Hotel.toJSON(v);
      });
    }
    message.meta !== undefined &&
      (obj.meta = message.meta ? MetaData.toJSON(message.meta) : undefined);
    return obj;
  },

  fromPartial(object: DeepPartial<HotelList>): HotelList {
    const message = createBaseHotelList();
    message.count = object.count ?? 0;
    message.priceMin = object.priceMin ?? 0;
    message.priceMax = object.priceMax ?? 0;
    message.pollUrl = object.pollUrl ?? "";
    message.itemOrder = object.itemOrder?.map((e) => e) || [];
    message.items = Object.entries(object.items ?? {}).reduce<{
      [key: string]: Hotel;
    }>((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = Hotel.fromPartial(value);
      }
      return acc;
    }, {});
    message.meta =
      object.meta !== undefined && object.meta !== null
        ? MetaData.fromPartial(object.meta)
        : undefined;
    return message;
  },
};

function createBaseHotelList_ItemsEntry(): HotelList_ItemsEntry {
  return { key: "", value: undefined };
}

export const HotelList_ItemsEntry = {
  encode(
    message: HotelList_ItemsEntry,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== undefined) {
      Hotel.encode(message.value, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): HotelList_ItemsEntry {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHotelList_ItemsEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.key = reader.string();
          break;
        case 2:
          message.value = Hotel.decode(reader, reader.uint32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): HotelList_ItemsEntry {
    return {
      key: isSet(object.key) ? String(object.key) : "",
      value: isSet(object.value) ? Hotel.fromJSON(object.value) : undefined,
    };
  },

  toJSON(message: HotelList_ItemsEntry): unknown {
    const obj: any = {};
    message.key !== undefined && (obj.key = message.key);
    message.value !== undefined &&
      (obj.value = message.value ? Hotel.toJSON(message.value) : undefined);
    return obj;
  },

  fromPartial(object: DeepPartial<HotelList_ItemsEntry>): HotelList_ItemsEntry {
    const message = createBaseHotelList_ItemsEntry();
    message.key = object.key ?? "";
    message.value =
      object.value !== undefined && object.value !== null
        ? Hotel.fromPartial(object.value)
        : undefined;
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var globalThis: any = (() => {
  if (typeof globalThis !== "undefined") return globalThis;
  if (typeof self !== "undefined") return self;
  if (typeof window !== "undefined") return window;
  if (typeof global !== "undefined") return global;
  throw "Unable to locate global object";
})();

type Builtin =
  | Date
  | Function
  | Uint8Array
  | string
  | number
  | boolean
  | undefined;

type DeepPartial<T> = T extends Builtin
  ? T
  : T extends Array<infer U>
  ? Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U>
  ? ReadonlyArray<DeepPartial<U>>
  : T extends {}
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isObject(value: any): boolean {
  return typeof value === "object" && value !== null;
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
